import { useEffect, useState } from 'react';
import { createApiCall } from 'services';

const cacheKey = (certificateId: string) => `vera_scan_verified:${certificateId}`;

/**
 * The cached verdict for this certificate, or null.
 *
 * VRG-252 — this stored `'1'` for verified and nothing otherwise, so it could not represent a
 * Connect scan. `'connected'` is stored under the same key: a distinct value on purpose, because
 * reusing `'1'` would have a returning navigation silently UPGRADE a Connect tap into "Authentic"
 * on the first frame — the exact overstatement this task exists to prevent, arriving by the back
 * door of a cache write.
 */
const readCache = (certificateId?: string): 'verified' | 'connected' | null => {
  if (!certificateId) return null;
  try {
    const raw = sessionStorage.getItem(cacheKey(certificateId));
    if (raw === '1') return 'verified';
    if (raw === 'connected') return 'connected';
    return null;
  } catch {
    return null;
  }
};

/**
 * VERA-455 — resolves the short-lived `vt` capability that a genuine tag scan
 * mints (via `/verify/:tag` -> redirect). Certificate DATA is public by design;
 * this hook decides whether to show the "freshly-verified / authentic" state.
 * A cloned or guessed certificate URL carries no valid `vt`, so it resolves
 * `false` and the UI must not claim authenticity.
 *
 * Once a valid `vt` resolves, the verdict is cached in sessionStorage per
 * certificate, so internal navigations or an auth bounce that drop the `vt`
 * query param don't downgrade a legitimately-scanned certificate mid-session.
 * The verdict is only ever raised to verified on a successful resolve — a stale
 * or expired `vt` never un-verifies a session that already proved a real scan.
 *
 * THREE STATES, NOT A BOOLEAN. This returned `boolean` and started at `false`,
 * which made "still checking" and "checked, not verified" the same value. Anything
 * rendering the verdict therefore had to assert "not verified" during the resolve
 * round-trip of a scan that was about to come back verified — showing the wrong
 * answer confidently, then correcting itself. `pending` is the state that lets a
 * caller say "checking" instead of guessing.
 *
 * `pending` only exists while a resolve is genuinely in flight. With no `vt` there
 * is nothing to check, so the answer is immediately `unverified` — not pending.
 */
/**
 * VRG-252 — FOUR states, because a tag being present and a tag being proven are different facts.
 *
 * - `verified`   — tapped, and the tag answered a challenge only the genuine chip can answer.
 * - `connected`  — tapped, and this tag's silicon carries no challenge to answer. A Connect
 *                  (NTAG 213) chip can be sealed so nobody rewrites it, but its address can be
 *                  copied onto another chip and the server cannot tell them apart. Tamper-resistant,
 *                  not tamper-evident.
 * - `unverified` — no tag was tapped at all. A shared or saved link. The ordinary case.
 * - `pending`    — the resolve is in flight.
 *
 * `connected` sits BETWEEN the other two and must not be collapsed into either. Folded into
 * `verified` it would lend a clonable tag cryptographic confidence; folded into `unverified` it
 * would discard a real fact — that a physical Vera tag for this item was present in someone's
 * hand — and tell them to go tap a tag they just tapped.
 */
export type ScanVerificationState = 'pending' | 'verified' | 'connected' | 'unverified';
export const useScanVerification = (
  certificateId: string | undefined,
  vt: string | null | undefined,
): ScanVerificationState => {
  // Seeded from the cache so a return visit within the session is verified on the
  // first frame with no flash of "checking".
  const [state, setState] = useState<ScanVerificationState>(
    () => readCache(certificateId) ?? (vt ? 'pending' : 'unverified'),
  );

  // Re-sync from the per-certificate session cache when the certificate changes.
  useEffect(() => {
    setState(readCache(certificateId) ?? (vt ? 'pending' : 'unverified'));
    // `vt` intentionally included: dropping the token mid-session must not restart a
    // resolve, but arriving WITH one must move us back to pending.
  }, [certificateId, vt]);

  // Validate a freshly-minted `vt` from the scan redirect against the backend.
  useEffect(() => {
    if (!certificateId || !vt) return;
    let active = true;
    (async () => {
      try {
        const res = await createApiCall({
          url: `/verify/resolve?vt=${encodeURIComponent(vt)}&certificate_id=${encodeURIComponent(
            certificateId,
          )}`,
          throwError: true,
        });
        if (!active) return;

        // VRG-252 — `verified` and `assurance` are separate answers and the endpoint returns both.
        // A Connect tap resolves `{ verified: false, assurance: 'static' }`: the token is genuine
        // and proves a physical tap, and the tap did not establish authenticity.
        //
        // The check is ordered `verified` first and reads `assurance` only on the false branch, so
        // a response from an older server (no `assurance` field) lands in `unverified` — the safe
        // direction. Nothing here can invent `connected` out of a missing field.
        if (res?.data?.verified) {
          try {
            sessionStorage.setItem(cacheKey(certificateId), '1');
          } catch {
            /* sessionStorage unavailable (private mode) — verdict stays in-memory */
          }
          setState('verified');
        } else if (res?.data?.assurance === 'static' && res?.data?.certificateId) {
          // `certificateId` is only returned when the token itself validated, so requiring it is
          // what separates "a real Connect tap" from "a forged or expired token that happens to
          // carry the word static".
          try {
            sessionStorage.setItem(cacheKey(certificateId), 'connected');
          } catch {
            /* sessionStorage unavailable (private mode) — verdict stays in-memory */
          }
          setState((prev) => (prev === 'verified' ? prev : 'connected'));
        } else {
          // Resolved, and the answer is no. Distinct from pending.
          setState((prev) => (prev === 'verified' || prev === 'connected' ? prev : 'unverified'));
        }
      } catch {
        // Fail closed, but STOP PENDING — a hung or failed resolve must not leave the
        // UI spinning forever. Never downgrade a session that already proved a real tap.
        if (active) {
          setState((prev) => (prev === 'verified' || prev === 'connected' ? prev : 'unverified'));
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [certificateId, vt]);

  return state;
};
